import { useCallback, useEffect, useState } from "react";

import { SpinnerLoader } from "components/loader/loader";
import { PageFooter } from "components/shared/page-footer";
import UnauthLayout from "components/shared/unauth-layout";
import { ORIGINS } from "consts";
import { useDecodeData } from "data/ss-data-hooks";
import { usePaperlessDocumentsEnrollFromEmailMutation } from "gql/__generated__/hooks";
import { logException, trackEvent, urlSearchParamsObject } from "utils";

import {
  AlreadyEnrolledStatus,
  EnrollmentErrorStatus,
  EnrollmentSuccessfulStatus,
} from "./paperless-enrollment-status";
import styles from "./paperless-enrollment.module.scss";

enum enrollmentStatusStates {
  SUCCESS,
  CONFLICT,
  ERROR,
  LOADING,
}

const trackPaperlessEnrollmentEvent = async (eventName, data) => {
  trackEvent(eventName, {
    ...data,
    enrollmentOrigin: ORIGINS.PAPERLESS_ENROLLMENT_EMAIL,
  });
};

export const PaperlessEnrollment = () => {
  const [paperlessDocumentsEnrollFromEmail] =
    usePaperlessDocumentsEnrollFromEmailMutation();

  const [currentState, setCurrentState] = useState(
    enrollmentStatusStates.LOADING
  );

  const enrollInPaperlessDocuments = useCallback(
    async (encodedData: string) => {
      try {
        const { data } = await paperlessDocumentsEnrollFromEmail({
          variables: { data: encodedData },
        });

        const { success, errorCode, errors } =
          data?.paperlessDocumentsEnrollFromEmail ?? {};

        if (success) {
          return {
            event: "Paperless Documents Enrolled",
            state: enrollmentStatusStates.SUCCESS,
          };
        } else {
          return {
            event: "Paperless Documents Enrollment Error Displayed",
            state:
              errorCode === 409
                ? enrollmentStatusStates.CONFLICT
                : enrollmentStatusStates.ERROR,
            errorCode,
            errorMessage: errors?.length && errors[0],
          };
        }
      } catch (err) {
        logException(err);
        return {
          event: "Paperless Documents Enrollment Error Displayed",
          state: enrollmentStatusStates.ERROR,
          errorCode: 500,
          errorMessage: "Server error",
        };
      }
    },
    [paperlessDocumentsEnrollFromEmail]
  );

  const { data: encodedData } = urlSearchParamsObject();
  const { data: decodedUserData, isLoading: isDecoding } =
    useDecodeData(encodedData);

  useEffect(() => {
    if (isDecoding) {
      return;
    }
    (async () => {
      trackPaperlessEnrollmentEvent(
        "Paperless Documents Enrollment Page Loaded",
        decodedUserData
      );
      const { event, state, ...rest } = await enrollInPaperlessDocuments(
        encodedData
      );
      trackPaperlessEnrollmentEvent(event, {
        ...decodedUserData,
        ...rest,
      });
      setCurrentState(state);
    })();
  }, [decodedUserData, isDecoding, enrollInPaperlessDocuments]);

  if (currentState === enrollmentStatusStates.LOADING) {
    return (
      <UnauthLayout size="lg">
        <div className={styles.loadingWrapper}>
          <SpinnerLoader firstMessage="Enrollment in progress" />
        </div>
      </UnauthLayout>
    );
  }

  return (
    <>
      {currentState === enrollmentStatusStates.SUCCESS && (
        <EnrollmentSuccessfulStatus />
      )}
      {currentState === enrollmentStatusStates.CONFLICT && (
        <AlreadyEnrolledStatus />
      )}
      {currentState === enrollmentStatusStates.ERROR && (
        <EnrollmentErrorStatus />
      )}
      <PageFooter unauthenticated />
    </>
  );
};
